
import React from 'react';
import { createRoot } from 'react-dom/client';
import { defineCustomElements, applyPolyfills } from 'blip-ds/loader';
import { ToastProvider } from '@/core/components/ToastProvider/ToastProvider';

let blipDSPromise = null;

/**
 * Resets the Blip DS promise for testing purposes.
 */
export const resetBlipDSInitialization = () => {
  blipDSPromise = null;
};

/**
 * Initializes Blip DS web components. Ensures it's only called once.
 * @returns {Promise<void>}
 */
export const initializeBlipDS = () => {
  if (!blipDSPromise) {
    blipDSPromise = applyPolyfills()
      .then(() => defineCustomElements())
      .catch(err => {
        console.error('[Blip-DS] Initialization error:', err);
        blipDSPromise = null; // Allow retry on failure
      });
  }
  return blipDSPromise;
};

/**
 * Parses a string attribute as JSON, falling back to a comma-separated list of strings.
 * If JSON parsing succeeds, returns the parsed JSON value (array, object, primitive, etc.).
 * If JSON parsing fails, returns an array of trimmed, non-empty strings split by commas.
 * @param {string|null|undefined} value
 * @returns {any|undefined}
 */
export const parseListAttribute = (value) => {
  if (!value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed;
  } catch {
    return value
      .split(',')
      .map(entry => entry.trim())
      .filter(Boolean);
  }
};

/**
 * Registers a React component as a Web Component
 * @param {string} tagName 
 * @param {React.ComponentType} Component 
 * @param {Function} [getProps] 
 */
export const registerReactWebComponent = (tagName, Component, getProps) => {
  class ReactElement extends HTMLElement {
    constructor() {
      super();
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this, { attributes: true });
    }

    connectedCallback() {
      this.mount();
    }

    disconnectedCallback() {
      this.unmount();
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    unmount() {
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
    }

    update() {
      this.mount();
    }

    mount() {
      // Ensure Blip DS is initialized before rendering components that use it
      initializeBlipDS();

      if (!this.root) {
        this.root = createRoot(this);
      }

      const props = getProps ? getProps(this) : {};

      const locale = this.getAttribute('locale') || 'pt';

      const render = () => {
        if (this.root) {
          this.root.render(
            React.createElement(
              ToastProvider,
              null,
              React.createElement(Component, { ...props, locale })
            )
          );
        }
      };

      // Use a microtask to avoid immediate re-renders during setup
      queueMicrotask(render);
    }
  }

  // Ensure unique class names or at least unique registration per tag
  if (!customElements.get(tagName)) {
    customElements.define(tagName, ReactElement);
  } else {
    // If already defined, we might need a way to reuse or ignore
    console.warn(`[WebComponent] Tag "${tagName}" is already defined.`);
  }
};
